import moment from "moment-timezone";

const formatUsPhone = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  const d =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (d.length === 10) {
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return (raw || "").trim();
};

export const getParishAddressLines = (parish) => {
  if (!parish) return [];
  const cityState = [parish.city, parish.state].filter(Boolean).join(", ");
  const cityStateZip = [cityState, parish.zip].filter(Boolean).join(" ").trim();
  return [parish.address, cityStateZip].filter(Boolean);
};

export const normalizeExternalLink = (link) => {
  const trimmed = (link || "").trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const parseRetreatContactInfo = (rawDesc) => {
  let emails = [];
  const phones = [];

  if (rawDesc && typeof document !== "undefined") {
    const html = rawDesc
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const mailtoEmails = Array.from(
      tempDiv.querySelectorAll('a[href^="mailto:"]'),
    )
      .map((el) =>
        (el.getAttribute("href") || "")
          .replace(/^mailto:/i, "")
          .split("?")[0]
          .trim(),
      )
      .filter(Boolean);

    let text = (tempDiv.textContent || tempDiv.innerText || "").replace(
      /\u00a0/g,
      " ",
    );
    text = text.replace(/(@[a-zA-Z0-9.-]+\.[a-z]{2,24})([A-Z])/g, "$1\n$2");

    const emailMatches = text.match(
      /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-z]{2,24}(?![a-z])/g,
    );
    emails = Array.from(
      new Set([...(mailtoEmails || []), ...(emailMatches || [])]),
    );

    const phoneRegex = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;
    const seenNumbers = new Set();
    let match;
    while ((match = phoneRegex.exec(text)) !== null) {
      const number = formatUsPhone(match[0]);
      if (!number || seenNumbers.has(number)) continue;
      seenNumbers.add(number);

      const before = text.slice(Math.max(0, match.index - 48), match.index);
      const lineBreak = Math.max(
        before.lastIndexOf("\n"),
        before.lastIndexOf("\r"),
      );
      const prefix = before
        .slice(lineBreak + 1)
        .replace(/^[\s(]+/, "")
        .replace(/[\s(\-–:)]+$/g, "")
        .trim();

      let name = "";
      if (prefix && /^[A-Za-z][A-Za-z .'-]*$/.test(prefix)) {
        name = prefix.replace(/\s+/g, " ");
      } else {
        const prevLine = before
          .slice(0, lineBreak >= 0 ? lineBreak : 0)
          .split(/\n/)
          .map((line) => line.trim())
          .filter(Boolean)
          .pop();
        if (prevLine && /^[A-Za-z][A-Za-z .'-]*$/.test(prevLine)) {
          name = prevLine.replace(/\s+/g, " ");
        }
      }

      phones.push({ name, number });
    }
  }

  return { emails, phones };
};

export const formatCoupleName = (couple) => {
  if (!couple) return "";
  return [
    couple.hisFirstName,
    couple.herFirstName ? `& ${couple.herFirstName}` : "",
    couple.hisLastName,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
};

export const formatRetreatLanguage = (language) => {
  if (!language) return "";
  if (language === "spanish") return "Español";
  if (language === "english") return "English";
  return `${language[0].toUpperCase()}${language.slice(1)}`;
};

export const formatRetreatDateRange = (retreat) => {
  if (!retreat?.dateFrom || !retreat?.dateTo) return "";
  const from = moment(retreat.dateFrom).tz("America/New_York");
  const to = moment(retreat.dateTo).tz("America/New_York");
  if (!from.isValid() || !to.isValid()) return "";
  if (from.isSame(to, "day")) return from.format("MMM D, YYYY");
  if (from.isSame(to, "month")) {
    return `${from.format("MMM D")} – ${to.format("D")}, ${from.format("YYYY")}`;
  }
  return `${from.format("MMM D")} – ${to.format("MMM D")}, ${from.format("YYYY")}`;
};

export const getRetreatParishName = (retreat) => {
  if (!retreat) return "";
  if (retreat.language === "spanish" && retreat?.myparish?.spanishParish) {
    return retreat.myparish.spanishParish;
  }
  return retreat?.myparish?.parish || retreat.parishName || retreat.title || "";
};

export const getRetreatDayName = (dateString, language) => {
  const dateInUSTimezone = moment(dateString).tz("America/New_York");
  const days =
    language === "spanish"
      ? [
          "Domingo",
          "Lunes",
          "Martes",
          "Miércoles",
          "Jueves",
          "Viernes",
          "Sábado",
        ]
      : [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ];
  return days[dateInUSTimezone.day()] || "";
};
